import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  BuildingOffice2Icon,
  UsersIcon,
  CreditCardIcon,
  EnvelopeIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  XMarkIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ShoppingCartIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';
import { getGroupedRoutes, GROUP_LABELS, type AdminRoute } from '../routes';
import { useAuth, VERSION_INFO } from '../../../contexts/AuthContext';

const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  HomeIcon,
  BuildingOffice2Icon,
  UsersIcon,
  CreditCardIcon,
  EnvelopeIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  ShoppingCartIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  BuildingStorefrontIcon,
};

interface AdminSidebarProps {
  open: boolean;
  onClose: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ open, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const grouped = getGroupedRoutes();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  // Close sidebar on Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (open) onClose();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close profile menu on outside click
  useEffect(() => {
    if (!showProfileMenu) return;
    const handler = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showProfileMenu]);

  const isActive = (route: AdminRoute) => {
    if (route.path === '/dashboard/super-admin') {
      return location.pathname === route.path;
    }
    return location.pathname === route.path || location.pathname.startsWith(route.path + '/');
  };

  const handleLogout = () => {
    setShowProfileMenu(false);
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  const sidebarContent = (
    <div className="flex flex-col h-full bg-slate-900 text-white">
      {/* Logo area */}
      <div className="h-16 flex items-center px-5 border-b border-slate-700/60 shrink-0">
        <img
          src="/images/aaywa-logo.png"
          alt="eSanza"
          className="h-8 w-8 rounded-lg object-contain bg-white p-0.5 shrink-0"
        />
        <div className="ml-3 min-w-0">
          <span className="block text-sm font-bold text-white leading-tight">eSanza Platform</span>
          <span className="block text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">Console</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5" role="navigation" aria-label="Platform navigation">
        {grouped.map(({ group, routes }) => (
          <div key={group}>
            <p className="px-3 mb-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              {GROUP_LABELS[group] || group}
            </p>
            <div className="space-y-0.5">
              {routes.map((route) => {
                const Icon = ICON_MAP[route.icon || 'HomeIcon'] || HomeIcon;
                const active = isActive(route);
                return (
                  <Link
                    key={route.key}
                    to={route.path}
                    onClick={onClose}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                      active
                        ? 'bg-emerald-600/15 text-emerald-400 border border-emerald-500/20 shadow-sm shadow-emerald-500/5'
                        : 'text-slate-300 hover:text-white hover:bg-slate-800'
                    }`}
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon className={`w-5 h-5 shrink-0 ${active ? 'text-emerald-400' : 'text-slate-400'}`} />
                    <span className="truncate">{route.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Owner identity with dropdown */}
      <div className="px-3 py-3 border-t border-slate-700/60 shrink-0" ref={profileRef}>
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
          onClick={() => setShowProfileMenu(!showProfileMenu)}
        >
          <div className="w-8 h-8 rounded-lg bg-emerald-700 flex items-center justify-center text-xs font-bold text-emerald-100 shrink-0">
            {user?.full_name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-white truncate">{user?.full_name || 'Platform Owner'}</p>
            <p className="text-[11px] text-slate-500 truncate">Platform Owner</p>
          </div>
          <ChevronDownIcon className={`w-4 h-4 text-slate-500 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} />
        </div>

        {/* Profile dropdown */}
        {showProfileMenu && (
          <div className="mt-2 rounded-lg border border-slate-700 bg-slate-800 shadow-xl overflow-hidden">
            <button
              type="button"
              onClick={() => { setShowProfileMenu(false); navigate('/dashboard/platform-settings'); }}
              className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-700 transition-colors"
            >
              <Cog6ToothIcon className="w-4 h-4" />
              Platform Settings
            </button>
            <button
              type="button"
              onClick={() => { setShowProfileMenu(false); navigate('/dashboard/profile'); }}
              className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-700 transition-colors"
            >
              <UserCircleIcon className="w-4 h-4" />
              My Profile
            </button>
            <div className="border-t border-slate-700" />
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-slate-700 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign out
            </button>
          </div>
        )}
      </div>

      {/* Environment / version */}
      <div className="px-5 py-3 border-t border-slate-800 text-center shrink-0">
        <p className="text-[10px] text-slate-600">
          v{VERSION_INFO.backend_version || '1.0.0'}
          {VERSION_INFO.git_commit && VERSION_INFO.git_commit !== 'unknown'
            ? ` · ${VERSION_INFO.git_commit}`
            : ''}
          {VERSION_INFO.environment && VERSION_INFO.environment !== 'production'
            ? ` · ${VERSION_INFO.environment}`
            : ''}
        </p>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-slate-900/60" onClick={onClose} />
          <div className="fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-200">
            <div className="relative h-full">
              <button
                type="button"
                onClick={onClose}
                className="absolute top-3 right-3 z-10 p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
                aria-label="Close sidebar"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
              {sidebarContent}
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:w-64 lg:flex-col bg-slate-900 border-r border-slate-700/60">
        {sidebarContent}
      </div>
    </>
  );
};

export default AdminSidebar;
